import { useCallback, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useToast } from '../context/ToastContext';
import { normalizeRnQp, normalizeRnQpList, RnQpThread } from '../types/rnQpRequests';

const apiUrl = process.env.REACT_APP_API_URL ?? '';
const baseUrl = `${apiUrl}/api/rn-qp-requests`;

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return { 'x-auth-token': token || '', 'Content-Type': 'application/json' };
};

export const useRnQpRequests = () => {
  const { showToast } = useToast();
  const { t } = useTranslation();
  const [threads, setThreads] = useState<RnQpThread[]>([]);
  const [loading, setLoading] = useState(false);
  const [nextNumber, setNextNumber] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const loadThreads = useCallback(async (rnWorkOrder: string, targetPage = 1) => {
    setLoading(true);
    try {
      const response = await axios.get(baseUrl, {
        headers: authHeaders(),
        params: { rnWorkOrder, page: targetPage },
      });
      const data = normalizeRnQpList(response.data);
      setThreads((prev) => (targetPage === 1 ? data : [...prev, ...data]));
      setPage(Number(response.data?.page) || targetPage);
      setTotalPages(Number(response.data?.totalPages) || 1);
      if (response.data?.nextNumber) setNextNumber(response.data.nextNumber);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to load QP requests';
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const refreshNextNumber = useCallback(async (rnWorkOrder: string) => {
    try {
      const response = await axios.get(`${baseUrl}/next-number`, {
        headers: authHeaders(),
        params: { rnWorkOrder },
      });
      const value = response.data?.data?.number || response.data?.number || '';
      setNextNumber(value);
      return value;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to get request number';
      showToast(message, 'error');
      return '';
    }
  }, [showToast]);

  const loadMore = useCallback(async (rnWorkOrder: string) => {
    if (loading || page >= totalPages) return;
    await loadThreads(rnWorkOrder, page + 1);
  }, [loadThreads, loading, page, totalPages]);

  const createThread = useCallback(async (payload: {
    rnWorkOrder: string;
    subject: string;
    recipients: string[];
    text: string;
    dateTo: string | null;
  }) => {
    try {
      const response = await axios.post(baseUrl, payload, { headers: authHeaders() });
      const row = normalizeRnQp(response.data);
      if (row) setThreads((prev) => [row, ...prev]);
      return row;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to send QP request';
      showToast(message, 'error');
      return null;
    }
  }, [showToast]);

  const addMessage = useCallback(async (id: string, text: string, dateTo?: string | null) => {
    try {
      const response = await axios.post(
        `${baseUrl}/${id}/messages`,
        { text, ...(dateTo !== undefined ? { dateTo } : {}) },
        { headers: authHeaders() }
      );
      const row = normalizeRnQp(response.data);
      if (row) setThreads((prev) => prev.map((item) => (item._id === row._id ? row : item)));
      return row;
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to send reply';
      showToast(message, 'error');
      return null;
    }
  }, [showToast]);

  const decide = useCallback(async (id: string, payload: {
    decision: 'approved' | 'rejected';
    pin: string;
    reason: string;
    checklist: { key: string; checked: boolean }[];
  }) => {
    try {
      const response = await axios.post(`${baseUrl}/${id}/decision`, payload, { headers: authHeaders() });
      const row = normalizeRnQp(response.data);
      if (row) setThreads((prev) => prev.map((item) => (item._id === row._id ? row : item)));
      if (row?.releaseTransfer?.number) {
        showToast(t('rnQpRequests.approvedWithTransfer', { number: row.releaseTransfer.number }), 'success');
      }
      return row;
    } catch (err: any) {
      // 409 refusals of the automatic release transfer carry a messageKey (errors.rnQpRequests.*).
      const messageKey = err.response?.data?.messageKey;
      const detail = err.response?.data?.detail;
      const message = err.response?.data?.message || err.message || 'Failed to record QP decision';
      showToast(messageKey ? `${t(messageKey)}${detail ? ` ${detail}` : ''}` : message, 'error');
      return null;
    }
  }, [showToast, t]);

  return {
    threads,
    loading,
    nextNumber,
    page,
    totalPages,
    loadThreads,
    loadMore,
    refreshNextNumber,
    createThread,
    addMessage,
    decide,
  };
};
